import { api } from "@/axios";

export const whatsappApi = {
  /**
   * Fetch WhatsApp messages
   * @param {Object} params - Query parameters
   * @param {number} params.page - Page number (default: 1)
   * @param {number} params.limit - Items per page (default: 10)
   * @param {string} params.status - Filter by status (optional)
   * @param {string} params.search - Search query (optional)
   * @returns {Promise} API response
   */
  async getMessages(params = {}) {
    try {
      const queryParams = {
        page: params.page || 1,
        limit: params.limit || 10,
        ...params
      };

      const response = await api.get("/whatsapp/messages", {
        params: queryParams
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching WhatsApp messages:", error);
      throw error;
    }
  },

  /**
   * Retry a failed message
   * @param {string} messageId - Message ID to retry
   * @returns {Promise} API response
   */
  async retryMessage(messageId) {
    try {
      const response = await api.post(`/whatsapp/messages/${messageId}/resend`);
      return response.data;
    } catch (error) {
      console.error("Error retrying message:", error);
      throw error;
    }
  },

  /**
   * Get message details by ID
   * @param {string} messageId - Message ID to fetch
   * @returns {Promise} API response
   */
  async getMessageById(messageId) {
    try {
      const response = await api.get(`/whatsapp/messages/${messageId}`);
      return response.data;
    } catch (error) {
      console.error("Error fetching message details:", error);
      throw error;
    }
  },

  /**
   * Delete a message
   * @param {string} messageId - Message ID to delete
   * @returns {Promise} API response
   */
  async deleteMessage(messageId) {
    try {
      const response = await api.delete(`/whatsapp/messages/${messageId}`);
      return response.data;
    } catch (error) {
      console.error("Error deleting message:", error);
      throw error;
    }
  },

  /**
   * Toggle WhatsApp messaging service on/off
   * @param {boolean} enabled - Whether to enable or disable the service
   * @returns {Promise} API response
   */
  async toggleMessaging(enabled) {
    try {
      const response = await api.put("/whatsapp/toggle-messaging", {
        enabled: enabled
      });
      return response.data;
    } catch (error) {
      console.error("Error toggling messaging service:", error);
      throw error;
    }
  },

  /**
   * Get current WhatsApp messaging service status
   * @returns {Promise} API response with service status
   */
  async getMessagingStatus() {
    try {
      const response = await api.get("/whatsapp/messaging-status");
      return response.data;
    } catch (error) {
      console.error("Error fetching messaging status:", error);
      throw error;
    }
  },

  /**
   * Get WhatsApp message settings
   * @returns {Promise} API response with message settings
   */
  async getMessageSettings() {
    try {
      const response = await api.get("/whatsapp/settings");
      return response.data;
    } catch (error) {
      console.error("Error fetching message settings:", error);
      throw error;
    }
  },

  /**
   * Create or update WhatsApp message settings
   * @param {Object} settings - Message settings object
   * @param {Object} settings.messageSettings - Message settings configuration
   * @param {boolean} settings.messageSettings.sendToDoctor - Enable/disable doctor messages
   * @param {boolean} settings.messageSettings.receptionMessage - Enable/disable reception messages
   * @returns {Promise} API response
   */
  async updateMessageSettings(settings) {
    try {
      const response = await api.post("/whatsapp/settings", settings);
      return response.data;
    } catch (error) {
      console.error("Error updating message settings:", error);
      throw error;
    }
  },

  /**
   * Partially update WhatsApp message settings
   * @param {Object} settings - Partial message settings object
   * @param {Object} settings.messageSettings - Partial message settings configuration
   * @returns {Promise} API response
   */
  async patchMessageSettings(settings) {
    try {
      const response = await api.patch("/whatsapp/settings", settings);
      return response.data;
    } catch (error) {
      console.error("Error patching message settings:", error);
      throw error;
    }
  },

  /**
   * Replace WhatsApp message settings
   * @param {Object} settings - Complete message settings object
   * @param {Object} settings.messageSettings - Complete message settings configuration
   * @returns {Promise} API response
   */
  async replaceMessageSettings(settings) {
    try {
      const response = await api.put("/whatsapp/settings", settings);
      return response.data;
    } catch (error) {
      console.error("Error replacing message settings:", error);
      throw error;
    }
  },

  /**
   * Delete WhatsApp message settings
   * @returns {Promise} API response
   */
  async deleteMessageSettings() {
    try {
      const response = await api.delete("/whatsapp/settings");
      return response.data;
    } catch (error) {
      console.error("Error deleting message settings:", error);
      throw error;
    }
  }
};
